import { ORG_COOKIE_KEY, LOCATION_COOKIE_KEY, tokenKey, permissionKeys } from 'utils/constants';
import { useAuth0 } from '@auth0/auth0-react';
import { getCurrentUser } from 'api/userApi';
import { orgAction, userAction, locationAction } from '../redux/actions';
import { useDispatch } from 'react-redux';
import { deleteCookie } from 'utils/cookie';
import { flattenDeep, get, isEmpty, map, orderBy } from 'lodash';
import { setOrgCookie, getOrganizations } from 'api/organizationApi';
import { setLocationCookie } from 'api/locationApi';

const useUser = () => {
  const NOTICE_BANNER_DISMISS_KEY = 'notice_banner_dismissed_by_user';
  const { logout } = useAuth0();
  const dispatch = useDispatch();

  const fetchCurrentUser = async () => {
    const { data } = await getCurrentUser();

    // HTTP 304 Not Modified returns an empty body — bail out gracefully since
    // the user data is already in Redux state from the previous successful response.
    if (!data || typeof data !== 'object') return;

    const checkPlatformAdmin = flattenDeep(map(data.roles, r => r.permissions)).includes(permissionKeys.PLATFORM_ADMIN);
    if (checkPlatformAdmin) data.organizations = get(await getOrganizations(), 'data.entities', []);
    data.organizations = orderBy(data.organizations, 'created', 'desc');
    const organizations = data.organizations;
    const organizationDefault = localStorage.getItem(ORG_COOKIE_KEY);
    try {
      if (!organizations || isEmpty(organizations)) {
        deleteCookie(ORG_COOKIE_KEY);
      } else if (!organizationDefault || !organizations.find((o: any) => o.id === organizationDefault)) {
        await handleSetTookie(organizations[0].id);
      } else {
        await handleSetTookie(organizationDefault);
      }
    } catch (error) {
      console.error('Failed to set org context cookie:', error);
    }
    dispatch(userAction.setUserInfo(data));
  };

  const handleLogout = () => {
    deleteCookie(tokenKey);
    localStorage.removeItem(ORG_COOKIE_KEY);
    localStorage.removeItem(NOTICE_BANNER_DISMISS_KEY);
    logout({ returnTo: `${window.location.origin}` });
  };

  const handleSetTookie = async (organizationId: any) => {
    const previousOrganizationId = localStorage.getItem(ORG_COOKIE_KEY);
    // Set Redux state and localStorage immediately so the app can render
    // even if the setOrgCookie API call fails or is slow.
    localStorage.setItem(ORG_COOKIE_KEY, organizationId);
    dispatch(orgAction.setOrgCookie(organizationId));
    // Only clear location context when org actually changes.
    // Keeping location for same-org refreshes prevents UI from snapping to first location.
    if (previousOrganizationId !== organizationId) {
      localStorage.removeItem(LOCATION_COOKIE_KEY);
      dispatch(locationAction.setLocationCookie(null));
    }
    await setOrgCookie(organizationId);
  };

  const handleSetLocationTookie = async (locationId: any, organizationId: any) => {
    if (locationId) {
      await setLocationCookie(locationId, organizationId);
      localStorage.setItem(LOCATION_COOKIE_KEY, locationId);
      dispatch(locationAction.setLocationCookie(locationId));
    }
  };

  return {
    handleLogout,
    fetchCurrentUser,
    handleSetTookie,
    handleSetLocationTookie
  };
};

export default useUser;
